'use client';

import React from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { getApiBaseUrl } from '@/lib/api';
import { loadRecaptcha, executeRecaptcha, unloadRecaptcha } from '@/lib/recaptcha';
import { useTranslations } from '@/hooks/useTranslations';

const inputClassName =
  'min-h-12 w-full rounded-2xl border border-white/10 bg-white/5 px-4 text-base text-slate-100 placeholder:text-slate-500 transition focus:border-[#ffb74d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/45';
const primaryButtonClassName =
  'inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-[#ffb74d] px-5 py-3 text-sm font-bold text-[#3a2100] shadow-lg shadow-[#ffb74d]/20 transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#15192b] disabled:cursor-not-allowed disabled:opacity-60';

export function ForgotPasswordForm() {
  const { t } = useTranslations();
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    loadRecaptcha().catch(() => {});
    return () => unloadRecaptcha();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const email = emailRef.current?.value?.trim() ?? '';
      const captchaToken = await executeRecaptcha('login');
      const response = await fetch(`${getApiBaseUrl()}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captchaToken }),
      });
      if (!response.ok) {
        let errorMessage = 'Something went wrong. Please try again.';
        try {
          const data = (await response.json()) as { message?: string };
          if (data.message) errorMessage = data.message;
        } catch {
          // ignore
        }
        throw new Error(errorMessage);
      }
      setSent(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4" role="status" aria-live="polite">
        <p className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm leading-6 text-emerald-100">
          {t('authFlow.forgotPassword.sentMessage')}
        </p>
        <p className="text-sm leading-6 text-slate-300">
          {t('authFlow.forgotPassword.sentHint')}{' '}
          <LocalizedLink
            href="/login"
            className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
          >
            {t('authFlow.backToSignIn')}
          </LocalizedLink>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} method="post" className="space-y-5">
      <div>
        <label htmlFor="forgot-email" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.email')}
        </label>
        <input
          ref={emailRef}
          id="forgot-email"
          className={inputClassName}
          placeholder={t('authFlow.fields.emailPlaceholder')}
          required
          type="email"
          name="email"
          autoComplete="email"
        />
      </div>
      <button
        type="submit"
        className={primaryButtonClassName}
        disabled={loading}
      >
        {loading ? t('authFlow.sending') : t('authFlow.forgotPassword.submit')}
      </button>
      {message && (
        <p className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}
